/**
 * Route Detail Panel - Slide-in panel for route details
 * Shows route summary with vertical timeline of stops
 */

import { useEffect, useState } from 'react';
import {
  X,
  MapPin,
  Package,
  Truck,
  UserPlus,
  UserMinus,
  Navigation,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  StopCircle,
  Check,
  List,
  BarChart3,
  Info,
  Scissors,
  Snowflake,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { Badge, CoolingBadge } from '../ui';
import { SLOT_STATE_LABELS, getSlotStateBadgeClasses } from '../../utils/pickupSlotStatus';
import type { Route, RouteStop } from '../../types';
import type { RouteStopWithStatus, DriverRecommendations } from '../../services/routes.api';
import { getDriverRecommendations, splitRoute } from '../../services/routes.api';
import { computeRouteTotals } from '../../utils/routeTotals';
import { TaskTimingPanel } from './TaskTimingPanel';
import { buildMerchantGroupColorMap, getMerchantBandColor } from '../../utils/merchantGroups';
import { useCan } from '../../contexts/useAuth';

interface Driver {
  driver_uid: string;
  full_name: string;
}

interface RouteDetailPanelProps {
  route: Route;
  stops: RouteStop[];
  drivers: Driver[];
  isOpen: boolean;
  onClose: () => void;
  onAssignDriver: (routeId: string, driverUid: string) => void | Promise<void>;
  onRemoveDriver?: (routeId: string) => void | Promise<void>;
  onDeleteRoute?: (routeId: string) => void | Promise<void>;
  onRemoveStop: (routeId: string, stopId: string) => Promise<void>;
  onAddStop?: () => void;
  onRefresh: () => void;
  onStopClick?: (stop: RouteStop) => void;
  stopsWithStatus?: RouteStopWithStatus[];
  onMarkTaskAsDone?: (taskUid: string, stopId: string) => Promise<void>;
  isDeleting?: boolean;
}

export default function RouteDetailPanel({
  route,
  stops,
  drivers,
  isOpen,
  onClose,
  onAssignDriver,
  onRemoveDriver,
  onDeleteRoute,
  onRemoveStop,
  onAddStop,
  onRefresh,
  onStopClick,
  stopsWithStatus,
  onMarkTaskAsDone,
  isDeleting = false,
}: RouteDetailPanelProps) {
  const can = useCan();
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemovingDriver, setIsRemovingDriver] = useState(false);
  const [isRemovingStop, setIsRemovingStop] = useState<string | null>(null);
  // Split mode: pick stops, hand them to another PA as a new route.
  const [splitMode, setSplitMode] = useState(false);
  const [splitStopIds, setSplitStopIds] = useState<Set<string>>(new Set());
  const [splitDriverUid, setSplitDriverUid] = useState('');
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [isMarkingDone, setIsMarkingDone] = useState<string | null>(null);
  const [showDriverSelect, setShowDriverSelect] = useState(false);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stops' | 'timing'>('stops');
  const [recommendations, setRecommendations] = useState<DriverRecommendations | null>(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [recsError, setRecsError] = useState<string | null>(null);

  // Ranked zone-driver recommendations (flying distance + fit checks) load when
  // the officer opens the picker. Advisory only — the full list stays selectable.
  useEffect(() => {
    if (!showDriverSelect) return;
    let cancelled = false;
    setLoadingRecs(true);
    setRecsError(null);
    getDriverRecommendations(route._id)
      .then((recs) => {
        if (!cancelled) setRecommendations(recs);
      })
      .catch((err: unknown) => {
        // Never fail silently. An unranked list of bare names is
        // indistinguishable from "there is no rule", which is how QA read it
        // on 2026-08-12. Degrading to the plain list is still correct — the
        // officer must be able to assign — but they have to be told.
        if (cancelled) return;
        setRecommendations(null);
        setRecsError(err instanceof Error ? err.message : 'Driver ranking is unavailable.');
      })
      .finally(() => {
        if (!cancelled) setLoadingRecs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showDriverSelect, route._id]);

  const candidateLabel = (c: NonNullable<DriverRecommendations['candidates']>[number]): string => {
    const parts: string[] = [];
    if (c.distance_km != null) parts.push(`${c.distance_km} km`);
    // The driver's actual working window. It was already on the wire and being
    // discarded — "outside shift" alone tells an officer a driver is wrong
    // without telling them who would be right.
    if (c.shift_window?.start && c.shift_window?.end) {
      parts.push(`${c.shift_window.start}-${c.shift_window.end}`);
    }
    if (!c.shift_ok) parts.push('outside shift');
    if (!c.capacity_ok) parts.push('over capacity');
    if (c.cold_ok === false) parts.push('no refrigerated van');
    if (c.plan_fit === true) parts.push('fits');
    return parts.length ? ` — ${parts.join(', ')}` : '';
  };

  // Color-band merchants that have 2+ stops in this route so each group stands out.
  const merchantColors = buildMerchantGroupColorMap(stops);

  // Format time to Baghdad timezone
  const formatTime = (dateStr?: string): string => {
    if (!dateStr) return '--:--';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Baghdad',
      });
    } catch {
      return '--:--';
    }
  };

  // Handle driver assignment
  const handleAssignDriver = async (driverUid: string) => {
    setIsAssigning(true);
    try {
      await onAssignDriver(route._id, driverUid);
      setShowDriverSelect(false);
      onRefresh();
    } catch (error) {
      console.error('Failed to assign driver:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle driver removal
  const handleRemoveDriver = async () => {
    if (!onRemoveDriver) return;
    if (!confirm('Are you sure you want to remove the driver from this route?\n\nAll tasks will be unassigned from the driver.')) return;
    setIsRemovingDriver(true);
    try {
      await onRemoveDriver(route._id);
      onRefresh();
    } catch (error) {
      console.error('Failed to remove driver:', error);
    } finally {
      setIsRemovingDriver(false);
    }
  };

  // Handle route deletion
  const handleDeleteRoute = async () => {
    if (!onDeleteRoute) return;
    if (!confirm('Are you sure you want to delete this route?\n\nThis action cannot be undone.')) return;
    try {
      await onDeleteRoute(route._id);
      onClose();
    } catch (error) {
      console.error('Failed to delete route:', error);
    }
  };

  // Handle remove stop from route
  const handleRemoveStop = async (stopId: string, taskStatus?: string) => {
    const statusText = taskStatus ? ` (current status: ${taskStatus})` : '';
    if (!confirm(`Are you sure you want to remove this stop from the route?${statusText}\n\nThe task will become unassigned and can be added to another route.`)) return;
    setIsRemovingStop(stopId);
    try {
      await onRemoveStop(route._id, stopId);
      onRefresh();
    } catch (error) {
      console.error('Failed to remove stop:', error);
      alert('Failed to remove stop. Please try again.');
    } finally {
      setIsRemovingStop(null);
    }
  };

  // Handle mark task as done
  const handleMarkAsDone = async (taskUid: string, stopId: string, merchantName?: string) => {
    const stopName = merchantName || `Stop ${stopId}`;
    if (!confirm(`Are you sure you want to mark "${stopName}" as DONE?\n\nThis action will update the task status in the system.`)) return;
    
    if (!onMarkTaskAsDone) {
      alert('Mark as done functionality is not available.');
      return;
    }
    
    setIsMarkingDone(stopId);
    try {
      await onMarkTaskAsDone(taskUid, stopId);
      onRefresh();
    } catch (error) {
      console.error('Failed to mark task as done:', error);
      alert('Failed to mark task as done. Please try again.');
    } finally {
      setIsMarkingDone(null);
    }
  };

  // Handle stop click - select and zoom
  const handleStopClick = (stop: RouteStop) => {
    setSelectedStopId(stop._id);
    if (onStopClick) {
      onStopClick(stop);
    }
  };

  // Route summary totals. Cancelled stops are excluded from the count-based
  // totals and service time; distance/cost/total duration stay as the originally
  // planned values. Driving time is geometry-pure (matches the per-stop legs and
  // does not change when a stop is cancelled). The cancelled stop itself still
  // renders in the list below (with its "Cancelled" badge).
  const {
    activeStopCount,
    totalOrders,
    rtoCount,
    pickupCount,
    totalServiceTimeMin,
    totalDrivingTimeMin,
  } = computeRouteTotals(stops, route, stopsWithStatus);

  // Format duration in hours
  const formatDurationHours = (minutes: number): string => {
    return `${(minutes / 60).toFixed(1)}h`;
  };

  // Check driver state
  const isUnassigned = !route.driver_uid || route.driver_uid === '';
  const assignedDriverName =
    (typeof route.driver_name === 'string' && route.driver_name.trim())
      ? route.driver_name
      : drivers.find((d) => d.driver_uid === route.driver_uid)?.full_name;

  // Don't allow editing if route is completed
  const canEdit = route.status !== 'completed';

  // Server rule, mirrored so ops never builds a selection the API will reject.
  const toggleSplitStop = (stopId: string) => {
    setSplitError(null);
    setSplitStopIds((prev) => {
      const next = new Set(prev);
      if (next.has(stopId)) next.delete(stopId);
      else next.add(stopId);
      return next;
    });
  };

  const exitSplitMode = () => {
    setSplitMode(false);
    setSplitStopIds(new Set());
    setSplitDriverUid('');
    setSplitError(null);
  };

  const handleSplit = async () => {
    if (splitStopIds.size === 0) return;
    setIsSplitting(true);
    setSplitError(null);
    try {
      const driver = splitDriverUid
        ? { uid: splitDriverUid, name: drivers.find((d) => d.driver_uid === splitDriverUid)?.full_name }
        : undefined;
      const result = await splitRoute(route._id, [...splitStopIds], driver);
      exitSplitMode();
      onRefresh();
      // The split landed but core rejected the driver — say so rather than
      // showing a clean success for a half-done handover.
      if (result.assignWarning) setSplitError(result.assignWarning);
    } catch (err) {
      setSplitError(err instanceof Error ? err.message : 'Split failed');
    } finally {
      setIsSplitting(false);
    }
  };
  
  // Don't allow deleting completed stops
  const canDeleteStop = (taskStatus?: string) => {
    return taskStatus !== 'done';
  };
  
  // Can mark as done if task is not already done
  const canMarkAsDone = (taskStatus?: string) => {
    if (!taskStatus) return false;
    return taskStatus !== 'done' && taskStatus !== 'cancelled';
  };

  // Helper to get task status for a stop
  const getStopStatus = (stop: RouteStop): string | undefined => {
    if (!stopsWithStatus) return undefined;
    const stopWithStatus = stopsWithStatus.find(s => s.task_uid === stop.task_uid);
    return stopWithStatus?.task_status;
  };

  // Split eligibility, mirroring the server's rule so ops never composes a
  // selection the API will reject.
  //
  // Must stay BELOW getStopStatus: movableStops calls it during render, and as a
  // const it is in the temporal dead zone until this point. Declared above, it
  // threw "Cannot access 'getStopStatus' before initialization" on every panel
  // open — tsc does not catch it through the closure, and it reached prod.
  const canMoveStop = (stop: RouteStop) => stop.type !== 'depot' && getStopStatus(stop) !== 'done';
  const movableStops = stops.filter(canMoveStop);
  const canSplit = canEdit && route.status !== 'cancelled' && movableStops.length >= 2;

  // Helper to get status icon and color
  const getStatusDisplay = (status?: string) => {
    switch (status) {
      case 'done':
        return { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Done' };
      case 'in-progress':
        return { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'In Progress' };
      case 'arrived':
        return { icon: MapPin, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'Arrived' };
      case 'driving':
        return { icon: Truck, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Driving' };
      case 'cancelled':
        return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Cancelled' };
      case 'stopped':
        return { icon: StopCircle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Stopped' };
      case 'assigned':
        return { icon: Circle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Assigned' };
      case 'new':
        return { icon: Circle, color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', label: 'New' };
      default:
        return { icon: Circle, color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Unknown' };
    }
  };

  if (!isOpen) return null;

  // A zone that needed more than one vehicle yields "Zone C" and "Zone C 2";
  // surface the trailing index rather than repeating a near-identical name.
  const zoneSplitIndex =
    route?.zone_name && route.route_name !== route.zone_name
      ? route.route_name.slice(route.zone_name.length).trim() || null
      : null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl z-50 flex flex-col border-l border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {route.zone_name && (
              <span
                className="inline-block h-3 w-3 rounded-full flex-shrink-0 border border-gray-300"
                style={{ backgroundColor: route.zone_color ?? '#9CA3AF' }}
                title={`Zone color ${route.zone_color ?? ''}`}
              />
            )}
            <span className="truncate">{route.zone_name || route.route_name}</span>
            {/* "Zone C 2" is the zone's 2nd vehicle, not another zone — say so. */}
            {zoneSplitIndex && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 border"
                style={{
                  color: route.zone_color ?? '#6B7280',
                  borderColor: route.zone_color ?? '#9CA3AF',
                  backgroundColor: `${route.zone_color ?? '#9CA3AF'}14`,
                }}
                title={`Route ${zoneSplitIndex} of this zone — the zone needed more than one vehicle`}
              >
                #{zoneSplitIndex}
              </span>
            )}
            {/* Shown BEFORE the officer opens the driver list, so a refused
                assignment is never the first they hear of it. */}
            {route.requires_cold_chain && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700 flex-shrink-0 inline-flex items-center gap-0.5"
                title="This route carries temperature-controlled orders. It can only be assigned to a driver marked as running a refrigerated van in Schedules."
              >
                <Snowflake className="h-2.5 w-2.5" />
                Cold
              </span>
            )}
            {(route.run_slot?.startsWith('SHIFT') || route.run_slot === 'COLD') && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 flex-shrink-0"
                title={`Generated for ${route.run_slot}`}
              >
                {route.run_slot}
              </span>
            )}
          </h2>
          {zoneSplitIndex && (
            <p className="text-xs text-gray-500 truncate">
              Vehicle {zoneSplitIndex} for {route.zone_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Driver Assignment & Actions */}
      <div className="px-4 py-3 border-b border-gray-200">
        {/* Driver info */}
        <div className="flex items-center gap-2 mb-3">
          <div className={cn(
            "p-1.5 rounded-full flex-shrink-0",
            isUnassigned ? "bg-amber-100" : "bg-green-100"
          )}>
            {isUnassigned ? (
              <UserPlus className="h-4 w-4 text-amber-600" />
            ) : (
              <UserPlus className="h-4 w-4 text-green-600" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={cn(
              "text-sm font-medium",
              isUnassigned ? "text-amber-700" : "text-gray-900"
            )}>
              {isUnassigned ? 'No driver assigned' : (assignedDriverName || 'Driver assigned')}
            </p>
          </div>
        </div>

        {/* Driver select dropdown — ranked recommendation first, full list below (officer can always override) */}
        {showDriverSelect && (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <select
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onChange={(e) => {
                  if (e.target.value) handleAssignDriver(e.target.value);
                }}
                disabled={isAssigning || !can('routes.edit')}
                defaultValue=""
              >
                <option value="">
                  {loadingRecs ? 'Ranking zone drivers…' : 'Select driver...'}
                </option>
                {recommendations && recommendations.candidates.length > 0 && (
                  <optgroup label="Zone drivers (nearest first)">
                    {recommendations.candidates
                      .filter((c) => c.driver_uid !== route.driver_uid)
                      .map((c) => (
                        <option key={c.driver_uid} value={c.driver_uid}>
                          {c.recommended ? '★ ' : ''}
                          {c.driver_name}
                          {candidateLabel(c)}
                        </option>
                      ))}
                  </optgroup>
                )}
                <optgroup label={recommendations ? 'All drivers' : 'Drivers'}>
                  {drivers
                    .filter((d) => d.driver_uid !== route.driver_uid)
                    .filter((d) => !recommendations?.candidates.some((c) => c.driver_uid === d.driver_uid))
                    .map((driver) => (
                      <option key={driver.driver_uid} value={driver.driver_uid}>
                        {driver.full_name}
                      </option>
                    ))}
                </optgroup>
              </select>
              <button
                onClick={() => setShowDriverSelect(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {recommendations?.recommended_driver_uid && (() => {
              const rec = recommendations.candidates.find((c) => c.recommended);
              // The caption has to describe the driver actually recommended. A
              // refrigerated van can now be recommended for a cold route while
              // sitting outside the run window — the guard refuses the warm
              // alternative, so it is still the right call, but saying it "fits
              // shift" would be a lie the officer acts on.
              const fitsAdvisory = rec?.shift_ok !== false && rec?.capacity_ok !== false;
              return (
                <p className="mt-1 text-xs text-gray-500">
                  {fitsAdvisory
                    ? `★ recommended: nearest zone driver that fits shift, capacity${rec?.plan_fit === true ? ' and route plan' : ''}`
                    : '★ recommended: the nearest driver this route can legally be given — it does not fit their shift or capacity, so check before assigning'}
                </p>
              );
            })()}

            {/*
              Why the list looks the way it does. The service already works this
              out — it was being computed and thrown away, so an unranked list
              of bare names looked identical to a feature that was never built.
            */}
            {!loadingRecs && (recsError || (recommendations?.warnings?.length ?? 0) > 0) && (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <div className="min-w-0 space-y-1 text-xs text-amber-800">
                    {recsError && <p>Driver ranking unavailable — {recsError}</p>}
                    {recommendations?.warnings?.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                    {(recsError || recommendations?.candidates.length === 0) && (
                      <p className="text-amber-700">
                        The drivers below are listed unranked.
                        {route.requires_cold_chain
                          ? ' This route carries temperature-controlled orders — the refrigerated-van rule is still enforced when you assign, so an ordinary van will be refused.'
                          : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {canEdit && (
          <div className="flex items-center gap-2">
            {/* Assign / Change Driver */}
            {!showDriverSelect && (
              <button
                onClick={() => setShowDriverSelect(true)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                  isUnassigned
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                )}
              >
                <UserPlus className="h-4 w-4" />
                {isUnassigned ? 'Assign Driver' : 'Change Driver'}
              </button>
            )}

            {/* Remove Driver */}
            {!isUnassigned && onRemoveDriver && can('routes.edit') && (
              <button
                onClick={handleRemoveDriver}
                disabled={isRemovingDriver}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                title="Remove driver"
              >
                {isRemovingDriver ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserMinus className="h-4 w-4" />
                )}
              </button>
            )}

            {/* Delete Route */}
            {onDeleteRoute && can('routes.generate') && (
              <button
                onClick={handleDeleteRoute}
                disabled={isDeleting}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                title="Delete route"
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Route Summary Stats */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        {/* Hub Start Indicator */}
        {route.start_location_mode === 'hub_start' && (
          <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
            <Navigation className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Starts from Warehouse</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{totalOrders} orders</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{activeStopCount} stops</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{route.total_distance_km.toFixed(1)} km</span>
          </div>
          <div className="flex items-center gap-2 text-green-600">
            <span className="font-medium">${(route.total_cost || 0).toFixed(2)}</span>
          </div>
        </div>
        {/* RTO and Pickup counts */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
          <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">{pickupCount} Pickup</span>
          <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium">{rtoCount} RTO</span>
        </div>
        {/* Time breakdown */}
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-4 text-xs">
          <span className="text-gray-500">
            🛑 {formatDurationHours(totalServiceTimeMin)} service
          </span>
          <span className="text-blue-600">
            🚗 {formatDurationHours(totalDrivingTimeMin)} driving
          </span>
          <span className="text-green-600 font-medium">
            ⏱️ {formatDurationHours(route.total_duration_min)} total
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <div className="flex">
          <button
            onClick={() => setActiveTab('stops')}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'stops'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <div className="flex items-center justify-center gap-2">
              <List className="h-4 w-4" />
              <span>Stops</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('timing')}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'timing'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <div className="flex items-center justify-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span>Timing</span>
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'stops' ? (
        <>
        {/* Vertical Timeline of Stops */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="relative">
          {/* Warehouse Start - only show if hub_start */}
          {route.start_location_mode === 'hub_start' && (
            <div className="relative pl-10 pb-6">
              {/* Timeline connector line */}
              <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200" />
              {/* Timeline node - warehouse */}
              <div className="absolute left-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-600 border-2 border-blue-600 text-white">
                <Navigation className="h-3 w-3" />
              </div>
              {/* Warehouse content */}
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-700">Baghdad Sorting Center</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-600 rounded">START</span>
                </div>
                <div className="text-xs text-blue-500 mt-1">Drivers depart from warehouse</div>
              </div>
            </div>
          )}
          
          {stops.map((stop, index) => {
            const isDepot = stop.type === 'depot';
            const isRTO = stop.type === 'rto';
            const isCash = stop.type === 'cash';
            const isSelected = selectedStopId === stop._id;
            const isLastStop = index === stops.length - 1;
            const taskStatus = getStopStatus(stop);
            const statusDisplay = getStatusDisplay(taskStatus);
            const StatusIcon = statusDisplay.icon;
            const isDone = taskStatus === 'done';
            
            // Use actual driving time from previous stop if available, otherwise estimate.
            // totalDrivingTimeMin is geometry-pure, so a cancelled stop's service
            // time does not inflate per-stop drive estimates.
            const drivingTimeToStop = stop.duration_from_prev_min
              ? Math.round(stop.duration_from_prev_min)
              : (index === 0 ? 0 : Math.round(totalDrivingTimeMin / (stops.length + 1)));
            
            // Keep sequence number for all stops, RTO indicated by color
            const stopIcon = (index + 1).toString();

            // Merchant group color-band (only for merchants with 2+ stops in this route)
            const bandColor = getMerchantBandColor(stop, merchantColors);

            const movable = canMoveStop(stop);
            const checked = splitStopIds.has(stop._id);

            return (
              <div
                key={stop._id}
                onClick={() => (splitMode ? movable && toggleSplitStop(stop._id) : handleStopClick(stop))}
                className={cn(
                  'relative pl-10 pb-6 group',
                  splitMode && !movable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                )}
              >
                {/* Split selection — replaces the timeline node's click target */}
                {splitMode && (
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!movable}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSplitStop(stop._id)}
                    title={
                      movable
                        ? 'Move this stop to the new route'
                        : stop.type === 'depot'
                        ? 'Depot stops stay with the route'
                        : 'Already done — the driver has been here'
                    }
                    className="absolute -left-1 top-7 z-10 h-4 w-4 disabled:cursor-not-allowed"
                  />
                )}
                {/* Timeline connector line - only show if not the last stop */}
                {!isLastStop && (
                  <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200" />
                )}
                {/* Timeline node */}
                <div
                  className={cn(
                    "absolute left-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all",
                    isDepot
                      ? "bg-blue-600 border-blue-600 text-white"
                      : isDone
                        ? "bg-green-600 border-green-600 text-white"
                        : isRTO
                          ? "bg-red-600 border-red-600 text-white"
                        : isCash
                          ? "bg-amber-600 border-amber-600 text-white"
                          : isSelected
                            ? "bg-blue-600 border-blue-600 text-white scale-110"
                            : "bg-white border-blue-400 text-blue-600 group-hover:border-blue-600 group-hover:bg-blue-50"
                  )}
                >
                  {isDepot ? (
                    <MapPin className="h-3 w-3" />
                  ) : isDone ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    stopIcon
                  )}
                </div>

                {/* Stop content */}
                <div
                  className={cn(
                    "p-3 rounded-lg border transition-all",
                    isSelected
                      ? "border-blue-300 bg-blue-50 shadow-sm"
                      : "border-gray-200 bg-white group-hover:border-gray-300 group-hover:shadow-sm"
                  )}
                  style={{
                    borderLeft: bandColor ? `4px solid ${bandColor}` : undefined,
                    backgroundColor: bandColor && !isSelected ? `${bandColor}15` : undefined,
                  }}
                >
                  {/* Header with name, type badge, status badge and ETA */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {stop.merchant_name || stop.address || `Stop ${index + 1}`}
                        </span>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0",
                          isRTO ? "bg-red-100 text-red-700" : isCash ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                        )}>
                          {isRTO ? 'RTO' : isCash ? 'Cash' : 'Pickup'}
                        </span>
                        {taskStatus && (
                          <span className={cn(
                            "px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 flex items-center gap-1",
                            statusDisplay.bg,
                            statusDisplay.color
                          )}>
                            <StatusIcon className="h-2.5 w-2.5" />
                            {statusDisplay.label}
                          </span>
                        )}
                        {stop.pickup_slot_state && (
                          <Badge
                            variant="default"
                            className={cn('text-[10px] whitespace-nowrap', getSlotStateBadgeClasses(stop.pickup_slot_state))}
                            title={`Pickup slot ${stop.pickup_slot_code ?? ''} · ${SLOT_STATE_LABELS[stop.pickup_slot_state] ?? stop.pickup_slot_state}`}
                          >
                            {stop.pickup_slot_code ?? 'slot'}
                          </Badge>
                        )}
                      </div>
                      {stop.address && stop.merchant_name && (
                        <div className="text-xs text-gray-600 truncate mt-0.5">
                          {stop.address}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Mark as Done button */}
                      {onMarkTaskAsDone && can('tasks.mark_done') && canMarkAsDone(taskStatus) && stop.task_uid && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsDone(stop.task_uid, stop._id, stop.merchant_name);
                          }}
                          disabled={isMarkingDone === stop._id}
                          className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Mark task as done"
                        >
                          {isMarkingDone === stop._id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                      {/* Remove stop button */}
                      {canDeleteStop(taskStatus) && can('routes.edit') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveStop(stop._id, taskStatus);
                          }}
                          disabled={isRemovingStop === stop._id}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Remove stop from route"
                        >
                          {isRemovingStop === stop._id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Details row - parcels */}
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    {stop.parcel_count && stop.parcel_count > 0 && (
                      <span className="flex items-center gap-1 text-gray-500">
                        <Package className="h-3 w-3" />
                        {stop.parcel_count} orders
                      </span>
                    )}
                    <CoolingBadge count={stop.cooling_count} />
                  </div>
                  
                  {/* Time details row - arrival time and service time */}
                  <div className="flex items-center justify-between gap-3 mt-1.5 text-[10px] text-gray-500">
                    <div className="flex items-center gap-3">
                      <span>🕐 Arrival: {formatTime(stop.eta)}</span>
                      <span>⏱️ Service: {stop.service_time_min || 0}m</span>
                      {index > 0 && drivingTimeToStop > 0 && (
                        <span className="text-blue-500">
                          🚗 ~{drivingTimeToStop}m drive
                        </span>
                      )}
                    </div>
                    {/* Debug info icon */}
                    <div className="group/debug relative">
                      <Info className="h-3 w-3 text-gray-300 hover:text-gray-500 cursor-help" />
                      {/* Invisible bridge to keep tooltip visible when moving mouse */}
                      <div className="absolute bottom-0 right-0 w-full h-8 hidden group-hover/debug:block" />
                      <div className="absolute bottom-full right-0 mb-1 hidden group-hover/debug:block z-50">
                        <div className="bg-gray-900 text-white text-[10px] rounded-lg shadow-lg p-2 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">Task UID:</span>
                              <code className="bg-gray-800 px-1.5 py-0.5 rounded font-mono select-all cursor-text">
                                {stop.task_uid}
                              </code>
                            </div>
                            {stop.parent_uid && (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400">Parent UID:</span>
                                <code className="bg-gray-800 px-1.5 py-0.5 rounded font-mono select-all cursor-text">
                                  {stop.parent_uid}
                                </code>
                              </div>
                            )}
                            <div className="text-gray-400 text-[9px] mt-1 border-t border-gray-700 pt-1">
                              Click to select text
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Final leg - Return to Sorting Centre - no timeline line below */}
          <div className="relative pl-10 pb-0">
            {/* Timeline node - no connector line below this one */}
            <div className="absolute left-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-600 border-2 border-blue-600 text-white">
              <MapPin className="h-3 w-3" />
            </div>

            {/* Stop content */}
            <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-900">Baghdad Sorting Centre</span>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-600 rounded">END</span>
                  </div>
                  <div className="text-xs text-blue-600 mt-0.5">
                    Return to depot
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="flex items-center gap-1 text-blue-500">
                  <Navigation className="h-3 w-3" />
                  ~{(() => {
                    // Calculate return to depot time: geometry-pure driving minus the
                    // sum of all stop drive legs. totalDrivingTimeMin is geometry-pure,
                    // so a cancelled stop's service time does not skew the estimate.
                    const sumStopDriveTimes = stops.reduce((sum, s) => sum + (s.duration_from_prev_min || 0), 0);
                    const returnTime = Math.max(0, Math.round(totalDrivingTimeMin - sumStopDriveTimes));
                    return returnTime > 0 ? returnTime : Math.round(totalDrivingTimeMin / (stops.length + 1));
                  })()}m drive
                </span>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Split / Add Stop — outside scrollable area */}
        {splitMode ? (
          <div className="space-y-2 border-t border-gray-200 bg-purple-50 px-4 py-3">
            <p className="text-xs text-purple-900">
              {splitStopIds.size === 0
                ? 'Tick the stops to move onto a new route.'
                : `${splitStopIds.size} stop${splitStopIds.size === 1 ? '' : 's'} will move; ${
                    stops.length - splitStopIds.size
                  } stay on ${route.route_name}.`}
            </p>
            <select
              value={splitDriverUid}
              onChange={(e) => setSplitDriverUid(e.target.value)}
              className="w-full rounded-lg border border-purple-200 px-2 py-1.5 text-sm"
            >
              <option value="">Leave the new route unassigned</option>
              {drivers
                .filter((d) => d.driver_uid !== route.driver_uid)
                .map((d) => (
                  <option key={d.driver_uid} value={d.driver_uid}>
                    {d.full_name}
                  </option>
                ))}
            </select>
            {splitError && <p className="text-xs text-red-600">{splitError}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleSplit}
                disabled={splitStopIds.size === 0 || isSplitting || splitStopIds.size >= stops.length}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                title={
                  splitStopIds.size >= stops.length
                    ? 'Leave at least one stop behind — to move everything, change the driver instead'
                    : undefined
                }
              >
                {isSplitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />}
                {isSplitting ? 'Splitting…' : 'Create new route'}
              </button>
              <button
                onClick={exitSplitMode}
                disabled={isSplitting}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          (canEdit && onAddStop && can('routes.edit')) || (canSplit && can('routes.edit')) ? (
            <div className="flex gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
              {canEdit && onAddStop && can('routes.edit') && (
                <button
                  onClick={onAddStop}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-100"
                >
                  <Plus className="h-4 w-4" />
                  Add Stop to Route
                </button>
              )}
              {canSplit && can('routes.edit') && (
                <button
                  onClick={() => setSplitMode(true)}
                  className="flex items-center justify-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 transition-colors hover:bg-purple-100"
                  title="Move some stops onto a new route for another PA"
                >
                  <Scissors className="h-4 w-4" />
                  Split
                </button>
              )}
            </div>
          ) : null
        )}
        </>
      ) : (
        /* Task Timing Panel */
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <TaskTimingPanel routeId={route._id} />
        </div>
      )}
    </div>
  );
}
